/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { useState, useRef } from "react";
import styles from "./date-picker.module.scss";
import { useClickOutsideListener } from "@src/hooks/use-click-outside-listener";
import caretDownIcon from "@src/assets/svg/caret-down.svg";
import { useI18N } from "@src/language/language-context";

interface DatePickerProps {
  selectedDate: string | null;
  onDateChange: (date: string | null) => void;
  placeholder: string;
  disablePastDates?: boolean;
}

const cssClassName = "date-picker";

export const DatePicker = ({
  selectedDate,
  onDateChange,
  placeholder,
  disablePastDates = false,
}: DatePickerProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const dropdownRef = useRef<HTMLDivElement>(null);
  const i18n = useI18N();
  const texts = {
    expirationDateLabel: i18n("modules.datePicker.expirationDateLabel"),
  };

  useClickOutsideListener(dropdownRef, () => setIsOpen(false), isOpen);

  const formatDisplayDate = (dateString: string | null) => {
    if (!dateString) return null;

    const date = new Date(dateString);

    const year = date.getUTCFullYear();
    const month = date.getUTCMonth();
    const day = date.getUTCDate();

    return new Date(Date.UTC(year, month, day)).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };
  const formatToISOString = (date: Date) => {
    const { year, month, day } = {
      year: date.getFullYear(),
      month: date.getMonth(),
      day: date.getDate(),
    };

    return new Date(Date.UTC(year, month, day, 23, 59, 59, 999)).toISOString();
  };

  const isPastDate = (date: Date) => {
    if (!disablePastDates) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const compareDate = new Date(date);
    compareDate.setHours(0, 0, 0, 0);
    return compareDate < today;
  };

  const handleDateSelect = (date: Date) => {
    onDateChange(formatToISOString(date));
    setIsOpen(false);
  };

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];

    // Previous month days
    const prevMonthLastDay = new Date(year, month, 0).getDate();
    for (let i = startingDayOfWeek - 1; i >= 0; i--) {
      days.push({
        day: prevMonthLastDay - i,
        isCurrentMonth: false,
        date: new Date(year, month - 1, prevMonthLastDay - i),
      });
    }

    // Current month days
    for (let i = 1; i <= daysInMonth; i++) {
      days.push({
        day: i,
        isCurrentMonth: true,
        date: new Date(year, month, i),
      });
    }

    // Next month filler
    const remainingDays = 42 - days.length;
    for (let i = 1; i <= remainingDays; i++) {
      days.push({
        day: i,
        isCurrentMonth: false,
        date: new Date(year, month + 1, i),
      });
    }

    return days;
  };

  const navigateMonth = (direction: number) => {
    setCurrentMonth(
      new Date(
        currentMonth.getFullYear(),
        currentMonth.getMonth() + direction,
        1,
      ),
    );
  };

  const navigateYear = (direction: number) => {
    setCurrentMonth(
      new Date(
        currentMonth.getFullYear() + direction,
        currentMonth.getMonth(),
        1,
      ),
    );
  };

  const isSelectedDate = (date: Date) => {
    if (!selectedDate) return false;
    const selected = new Date(selectedDate);

    return (
      date.getFullYear() === selected.getUTCFullYear() &&
      date.getMonth() === selected.getUTCMonth() &&
      date.getDate() === selected.getUTCDate()
    );
  };

  const isToday = (date: Date) => {
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  };

  const monthName = currentMonth.toLocaleDateString("en-US", {
    month: "short",
    year: "numeric",
  });
  const days = getDaysInMonth(currentMonth);

  return (
    <div className={styles[cssClassName]} ref={dropdownRef}>
      <p className={styles[`${cssClassName}__label`]}>
        {texts.expirationDateLabel}
      </p>
      <div
        className={styles[`${cssClassName}__header`]}
        onClick={() => setIsOpen(!isOpen)}
      >
        <span
          className={`${styles[`${cssClassName}__selected-date`]} ${
            !selectedDate ? styles[`${cssClassName}__placeholder`] : ""
          }`}
        >
          {selectedDate ? formatDisplayDate(selectedDate) : placeholder}
        </span>
        <img
          className={styles[`${cssClassName}__caret`]}
          src={caretDownIcon}
          alt="Caret Down"
        />
      </div>

      {isOpen && (
        <div className={styles[`${cssClassName}__dropdown`]}>
          <div className={styles[`${cssClassName}__dropdown-inner`]}>
            <div className={styles[`${cssClassName}__calendar-header`]}>
              <button
                className={styles[`${cssClassName}__nav-button`]}
                onClick={() => navigateYear(-1)}
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path
                    d="M11 4L7 8L11 12"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M7 4L3 8L7 12"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
              <button
                className={styles[`${cssClassName}__nav-button`]}
                onClick={() => navigateMonth(-1)}
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path
                    d="M10 4L6 8L10 12"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
              <span className={styles[`${cssClassName}__month-year`]}>
                {monthName}
              </span>
              <button
                className={styles[`${cssClassName}__nav-button`]}
                onClick={() => navigateMonth(1)}
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path
                    d="M6 4L10 8L6 12"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
              <button
                className={styles[`${cssClassName}__nav-button`]}
                onClick={() => navigateYear(1)}
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path
                    d="M5 4L9 8L5 12"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M9 4L13 8L9 12"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>

            <div className={styles[`${cssClassName}__calendar-grid`]}>
              <div className={styles[`${cssClassName}__weekday-header`]}>
                {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((day) => (
                  <div key={day} className={styles[`${cssClassName}__weekday`]}>
                    {day}
                  </div>
                ))}
              </div>
              <div className={styles[`${cssClassName}__days-grid`]}>
                {days.map((dayObj, index) => {
                  const isPast = isPastDate(dayObj.date);
                  const cellClasses = [
                    styles[`${cssClassName}__day-cell`],
                    !dayObj.isCurrentMonth &&
                      styles[`${cssClassName}__day-cell--other-month`],
                    isToday(dayObj.date) &&
                      styles[`${cssClassName}__day-cell--today`],
                    isSelectedDate(dayObj.date) &&
                      styles[`${cssClassName}__day-cell--selected`],
                    isPast && styles[`${cssClassName}__day-cell--disabled`],
                  ]
                    .filter(Boolean)
                    .join(" ");

                  return (
                    <button
                      key={index}
                      className={cellClasses}
                      onClick={() => !isPast && handleDateSelect(dayObj.date)}
                      disabled={isPast}
                    >
                      {dayObj.day}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
